import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addSharePermissionToDocs, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Doc, GPanelPermissionsModel } from '../../../../../types';
import { useState } from 'react';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useFieldArray, useForm } from 'react-hook-form';
import DriveExpirationChip from '../../../drive/drive-search/drive-expiration-chip';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface DriveAddShareDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  selectedDocs: Doc[];
  changeLogRoles: string[];
  isAllUsers?: boolean;
  fromSharedDrives?: boolean;
}

export default function DriveAddShareDialog({
  open,
  handleClose,
  userEmail,
  selectedDocs,
  changeLogRoles,
  isAllUsers,
  fromSharedDrives,
}: DriveAddShareDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();

  const { control, watch, setValue, handleSubmit } = useForm<{
    aclResources: GPanelPermissionsModel[];
  }>({
    defaultValues: {
      aclResources: [
        {
          permId: 'new-share',
          userEmail: '',
          role: 'reader',
          type: 'user',
          permExpirationTime: 0,
          changeType: 'add',
        } as GPanelPermissionsModel,
      ],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'aclResources',
  });

  const watchedAcls = watch('aclResources');

  const areAllFolders = selectedDocs.every((doc) =>
    doc.mimeType.includes('folder')
  );

  const hasFolders = selectedDocs.some((doc) =>
    doc.mimeType.includes('folder')
  );

  const { mutate, isPending } = useMutation({
    mutationFn: (data: { aclResources: GPanelPermissionsModel[] }) => {
      const newPermission = data.aclResources[0];
      return addSharePermissionToDocs(
        userEmail,
        selectedDocs,
        newPermission.userEmail || '',
        newPermission.role,
        changeLogMessage,
        newPermission.permExpirationTime ?? undefined
      );
    },
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message:
          'The task to add share permissions to the selected documents or folders has started.',
      });
      setChangeLogMessage('');

      // Invalidate based on what types of items are selected
      const hasFolders = selectedDocs.some((doc) =>
        doc.mimeType.includes('folder')
      );
      const hasFiles = selectedDocs.some(
        (doc) => !doc.mimeType.includes('folder')
      );

      if (hasFolders) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveFolders],
        });
      }
      if (hasFiles) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments],
        });
      }

      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue starting the task to add share permissions. Please refresh and try again.',
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.create_drive_shares, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.create_drive_shares,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function onSubmit(data: { aclResources: GPanelPermissionsModel[] }) {
    if (
      isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanEditDocShares.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Add Share"
      actingAs={userEmail}
      dialogContent={
        <>
          <Typography>Share the selected documents to a user.</Typography>
          <Stack flexDirection="row" gap={2} sx={{ mt: 2 }}>
            <Box sx={{ width: 500 }}>
              <SharedAutocomplete
                sources={['users', 'groups']}
                autocompleteValue={watchedAcls[0]?.userEmail ?? ''}
                setAutoCompleteValue={(value) => {
                  setValue('aclResources.0.userEmail', value, {
                    shouldDirty: true,
                  });
                }}
              />
            </Box>

            {fields.map((field, index) => (
              <TextField
                key={field.id}
                inputProps={{
                  id: `permission-${index}`,
                }}
                InputLabelProps={{
                  htmlFor: `permission-${index}`,
                }}
                select
                label="Permission"
                value={watchedAcls[index]?.role ?? 'reader'}
                onChange={(e) =>
                  setValue(
                    `aclResources.${index}.role`,
                    e.target.value as any,
                    {
                      shouldDirty: true,
                    }
                  )
                }
                sx={{ minWidth: 150 }}
              >
                {!fromSharedDrives || !areAllFolders
                  ? [
                      <MenuItem key="reader" value={'reader'}>
                        Viewer
                      </MenuItem>,
                      <MenuItem key="commenter" value="commenter">
                        Commenter
                      </MenuItem>,
                      <MenuItem key="writer" value="writer">
                        Editor
                      </MenuItem>,
                    ]
                  : [
                      <MenuItem key="reader" value="reader">
                        Viewer
                      </MenuItem>,
                      <MenuItem key="commenter" value="commenter">
                        Commenter
                      </MenuItem>,
                      <MenuItem key="writer" value="writer">
                        Contributor
                      </MenuItem>,
                      <MenuItem key="fileOrganizer" value="fileOrganizer">
                        Content Manager
                      </MenuItem>,
                    ]}
              </TextField>
            ))}
          </Stack>
          {watchedAcls[0]?.userEmail && (
            <Stack>
              {fields.map((field, index) => {
                const currentRole = watchedAcls[index]?.role;

                const showExpirationChip = () => {
                  const isMixedSelection = !areAllFolders && hasFolders;
                  if (fromSharedDrives) {
                    if (isMixedSelection) {
                      // do not display expiration chip when both types are selected
                      // folders only allow expiration date for viewers
                      // docs only allow expiration date for contributor and commentor
                      return false;
                    }

                    if (areAllFolders) {
                      return currentRole === 'reader';
                    } else {
                      return currentRole !== 'reader';
                    }
                  }

                  // My Drive expiration chip display
                  // only display for viewer when selected is a folder, otherwise, display for all roles
                  return areAllFolders || isMixedSelection
                    ? currentRole === 'reader'
                    : true;
                };

                return showExpirationChip() ? (
                  <Box
                    key={field.id}
                    sx={{
                      mt: 1,
                      '& .MuiChip-root': {
                        position: 'static !important',
                        left: 'auto !important',
                        bottom: 'auto !important',
                        marginLeft: 0,
                      },
                    }}
                  >
                    <DriveExpirationChip
                      acl={field}
                      watchedAcl={watchedAcls[index]}
                      index={index}
                      control={control}
                      setValue={setValue}
                    />
                  </Box>
                ) : null;
              })}
            </Stack>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => handleSubmit(onSubmit)()}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: 'Add Share',
        action: handleSubmit(onSubmit),
        loading: isPending,
        disabled: isPending || !watchedAcls[0]?.userEmail,
      }}
    />
  );
}
